import { getElementValidationErrors, isElementValueEmpty } from './elements';
import { isFileElement } from './intakeLinks';

/**
 * WHEN A PUBLIC FORM CHANGES UNDER A VISITOR (KPR-515; keepr-api docs/SCHEMA.md
 * § Public forms, "409 form_changed"; the owner's ruling f09: "the form
 * reloads and their entered values are prepopulated on the form. Then they
 * fill the newly required missing element and submit again").
 *
 * The rules, as pure functions, so SharedFormPage and its tests share them:
 *
 *   formShape        what the page remembers about the form it showed — each
 *                    element's name, type and whether it was required. Names
 *                    the visitor was shown, nothing else.
 *   carryValues      which typed answers go back onto the form as it is now:
 *                    a question still asked, as the same type, never a file.
 *   changedQuestions which questions to point at: NEW ones, and ones NOW
 *                    REQUIRED that are empty, and ones whose type changed
 *                    under an answer (that answer could not be kept).
 *   the draft        the same answers in the visitor's own tab
 *                    (sessionStorage), so a reload after the owner fixes a
 *                    form that stopped brings them back.
 */

// Required ALWAYS, and the condition it is required UNDER (as served), kept
// apart: an element that was required only when another answer said so and
// is now required outright has changed, and must be pointed at.
const alwaysRequired = def => def?.options?.required === true;
const conditionOf = def => (Array.isArray(def?.options?.requiredWhen) && def.options.requiredWhen.length
	? JSON.stringify(def.options.requiredWhen) : '');

/**
 * [{ name, dataType, required, when, private }] for the elements a form
 * showed. `private`: asked only for the people who run the collection
 * (KPR-389) — an answer given under that promise must never be carried into
 * a question whose answer goes on the record (the independent review's H1).
 */
export function formShape(elements) {
	return (Array.isArray(elements) ? elements : [])
		.filter(def => def?.name)
		.map(def => ({
			name: def.name, dataType: `${def.dataType || ''}`, required: alwaysRequired(def), when: conditionOf(def), private: def.private === true,
		}));
}

/** The answer to `old` cannot stand as an answer to `def`: another type, or moved between the record and the private section. */
const notTheSameQuestion = (def, old) => Boolean(old) && (old.dataType !== `${def.dataType || ''}` || Boolean(old.private) !== (def.private === true));

const shapeByName = shape => new Map((Array.isArray(shape) ? shape : [])
	.filter(entry => entry && typeof entry.name === 'string')
	.map(entry => [entry.name, entry]));

/**
 * The answers to lay over the new form's starting values: `values` (by
 * element name) kept only for an element the form still asks, as the same
 * type it had in `oldShape`, and never a file — a file is bound to the
 * submission token it was sent under and travels as an upload, not a value.
 * Answers `{ carried, dropped }`: `dropped` names answers that could not be
 * kept (the question is gone, or its type changed), so the page can say so.
 */
export function carryValues(elements, values, oldShape) {
	const before = shapeByName(oldShape);
	const now = new Map((Array.isArray(elements) ? elements : []).filter(def => def?.name).map(def => [def.name, def]));
	const carried = {};
	const dropped = [];
	Object.entries(values && typeof values === 'object' ? values : {}).forEach(([name, value]) => {
		const def = now.get(name);
		const old = before.get(name);
		const answered = !isElementValueEmpty(def || old, value);
		// ⚠ Never carried across the private line, either way: a private
		// answer into a question the record keeps would publish what the
		// visitor gave in confidence; the reverse is dropped too, so the
		// visitor is asked again under what the form now says.
		if (!def || isFileElement(def) || notTheSameQuestion(def, old)) {
			if (answered && !(def && isFileElement(def))) dropped.push(name);
			return;
		}
		carried[name] = value;
	});
	return { carried, dropped };
}

/**
 * name → 'new' | 'required' | 'retyped', for the questions the visitor must
 * look at on the form as it is now, given the answers it holds now
 * (`values`, files included as their ids): a question the old form did not
 * ask; one that is unanswered and now required where it was not — optional
 * before, or required only under a condition that has since changed (or
 * been dropped for "always"); one whose type changed, so the answer it had
 * was dropped (`dropped`). A question required exactly as before is not
 * pointed at: the visitor was told already.
 */
export function changedQuestions(elements, oldShape, values, dropped = []) {
	const before = shapeByName(oldShape);
	const defs = (Array.isArray(elements) ? elements : []).filter(def => def?.name);
	const failing = getElementValidationErrors(defs, values || {});
	const lost = new Set(dropped || []);
	const marks = {};
	defs.forEach((def) => {
		const old = before.get(def.name);
		if (!old) { marks[def.name] = 'new'; return; }
		if (lost.has(def.name)) { marks[def.name] = Boolean(old.private) !== (def.private === true) ? 'moved' : 'retyped'; return; }
		const ruleChanged = alwaysRequired(def) !== Boolean(old.required) || conditionOf(def) !== `${old.when || ''}`;
		if (!old.required && ruleChanged && failing[def.name]) marks[def.name] = 'required';
	});
	return marks;
}

export const CHANGE_WORDS = Object.freeze({
	new: 'New question',
	required: 'Now required',
	retyped: 'Changed — answer again',
	moved: 'Moved — answer again',
});

// ---------------------------------------------------------------- the draft
//
// What the visitor typed, in THEIR OWN TAB's sessionStorage (the plan's
// Decision 6): never sent anywhere, never in a URL, never a file, and gone
// with the tab or a successful send. Every access is guarded — a private
// window, a full or blocked store, or a store that throws must leave the
// page working exactly as it does without one.

const DRAFT_VERSION = 1;
// ⚑ A draft older than this is no draft: the form may be on a shared tablet
// or a library computer, and the next person to reload the tab must not be
// handed the last one's answers hours later (the independent review's M2).
export const DRAFT_MAX_AGE_MS = 2 * 60 * 60e3;

/** The draft's key for a public form's address. */
export const draftKeyFor = (collectionSlug, formSlug) => `keepr.publicForm.draft:${collectionSlug || ''}/${formSlug || ''}`;

function store() {
	try {
		return typeof window !== 'undefined' && window.sessionStorage ? window.sessionStorage : null;
	} catch {
		return null;
	}
}

/** `{ cardId, shape, values }` or null (a `notes` kept before KPR-792 is not read). A draft that does not parse, or of another shape, is no draft. */
export function readDraft(key) {
	const s = store();
	if (!s || !key) return null;
	try {
		const raw = s.getItem(key);
		if (!raw) return null;
		const draft = JSON.parse(raw);
		if (!draft || draft.v !== DRAFT_VERSION || !Array.isArray(draft.shape) || !draft.values || typeof draft.values !== 'object') return null;
		if (!Number.isFinite(draft.at) || Date.now() - draft.at > DRAFT_MAX_AGE_MS || draft.at > Date.now() + 60e3) {
			s.removeItem(key);
			return null;
		}
		return {
			cardId: typeof draft.cardId === 'string' ? draft.cardId : null,
			shape: draft.shape,
			values: draft.values,
		};
	} catch {
		return null;
	}
}

/**
 * Keep the answers under `key`. Best effort. Never a file element's (a file
 * travels as an upload, bound to its token), and ⚑ never a PRIVATE answer:
 * those are the ones a visitor least wants left in a shared tab, and the
 * in-place reload (SharedFormPage) keeps them in memory anyway — only a
 * reload after the form stopped asks for them again.
 */
export function writeDraft(key, { cardId, elements, values }) {
	const s = store();
	if (!s || !key) return;
	const files = new Set((elements || []).filter(def => isFileElement(def) || def?.private === true).map(def => def.name));
	const kept = {};
	Object.entries(values || {}).forEach(([name, value]) => {
		if (!files.has(name) && value !== undefined) kept[name] = value;
	});
	try {
		s.setItem(key, JSON.stringify({ v: DRAFT_VERSION, at: Date.now(), cardId: cardId || null, shape: formShape(elements), values: kept }));
	} catch {
		// Full, blocked or gone: the page works without it.
	}
}

export function clearDraft(key) {
	const s = store();
	if (!s || !key) return;
	try { s.removeItem(key); } catch { /* nothing to do */ }
}
